import type { EntityConfigDefinition, DisplayColumnConfig, FilterConfig } from '../../types/entityConfig'
import { TABLE_SERVICE_RESPONSE, TABLE_PRODUCT } from '../../utils/displayFormatters'

const config: EntityConfigDefinition = {
  name: 'SERVICE_RESPONSE_MAPPING',
  idField: 'SERVICE_RESPONSE_MAPPING_ID',
  
  // Use optimized query for filtering
  useOptimizedQuery: true,
  optimizedQueryName: 'getFilteredServiceResponseMappings',
  supportsPagination: true,
  
  // Data loading configuration
  loadServiceOptions: true,
  loadProductOptions: true,
  
  // Filtering configuration
  hasFilters: true,
  requiresServiceFilter: true,
  requiresProductFilter: true,
  showEmptyUntilFiltered: true,
  
  // Form processing configuration
  keepAuditFieldsOnUpdate: false,
  
  // Fields to remove before mutations (display fields, etc.)
  fieldsToRemove: ['SERVICE_RESPONSE_DISPLAY', 'ORIGIN_PRODUCT_DISPLAY', 'SERVICE_DISPLAY', 'CHANGED_DATE', 'CHANGED_BY_USER_ID'],
  
  // Environment copy configuration
  preserveOnCopy: ['SERVICE_RESPONSE_MAPPING_ID'],
  
  // Environment comparison configuration
  comparisonConfig: {
    matchingFields: ['SERVICE_RESPONSE_ID', 'ORIGIN_PRODUCT_ID'],
    comparisonFields: ['SERVICE_RESPONSE_ID', 'ORIGIN_PRODUCT_ID', 'OUTPUT_PATH'],
    useDisplayValues: {
      'SERVICE_RESPONSE_ID': 'RESPONSE_PATH',
      'ORIGIN_PRODUCT_ID': 'PRODUCT_ID'
    },
    displayFieldMapping: {
      'SERVICE_RESPONSE_ID': 'RESPONSE_PATH',
      'ORIGIN_PRODUCT_ID': 'PRODUCT_ID'
    },
    displayFields: ['SERVICE_RESPONSE_MAPPING_ID', 'SERVICE_RESPONSE_ID', 'ORIGIN_PRODUCT_ID', 'OUTPUT_PATH'],
    resolveLookupsForBothEnvironments: true
  },
  
  // Integrated service selection for response filtering
  integratedFilters: {
    SERVICE_RESPONSE_ID: {
      filterField: 'SERVICE_ID',
      filterLabel: 'Service',
      filterSource: 'SERVICE',
      filterValueField: 'SERVICE_ID',
      filterDisplayField: 'URI',
      dependsOn: 'selectedServiceFilter'
    }
  },
  
  // Foreign key lookups for dropdown population and table display
  foreignKeys: {
    SERVICE_RESPONSE_ID: {
      table: 'SERVICE_RESPONSE',
      valueField: 'SERVICE_RESPONSE_ID',
      displayField: 'RESPONSE_PATH',
      displayFormat: TABLE_SERVICE_RESPONSE,
      dependsOn: ['SERVICE_ID', 'ORIGIN_PRODUCT_ID'],
      filterQuery: 'getFilteredServiceResponseMappings'
    },
    ORIGIN_PRODUCT_ID: {
      table: 'ORIGIN_PRODUCT',
      valueField: 'ORIGIN_PRODUCT_ID',
      displayField: 'PRODUCT_ID',
      displayFormat: TABLE_PRODUCT
    }
  },
  
  // Field lookups for display enhancement
  fieldLookups: {
    SERVICE_RESPONSE_ID: {
      lookupTable: 'SERVICE_RESPONSE',
      foreignKey: 'SERVICE_RESPONSE_ID',
      displayField: 'RESPONSE_PATH',
      displayFormat: TABLE_SERVICE_RESPONSE
    },
    ORIGIN_PRODUCT_ID: {
      lookupTable: 'ORIGIN_PRODUCT',
      foreignKey: 'ORIGIN_PRODUCT_ID',
      displayField: 'PRODUCT_ID',
      displayFormat: TABLE_PRODUCT
    }
  },
  
  // Custom filters for service dropdown
  customFilters: {
    pscuClient: {
      label: 'Filter by PSCU Client ID',
      field: 'PSCU_CLIENT_ID',
      type: 'pscuClient',
      required: false,
      showAlways: true,
      order: 1
    },
    product: {
      label: 'Filter by Product',
      field: 'ORIGIN_PRODUCT_ID',
      type: 'product',
      required: true,
      showAlways: true,
      order: 2
    },
    service: {
      label: 'Filter by Service',
      field: 'SERVICE_ID',
      type: 'service',
      required: true,
      showAlways: true,
      order: 3
    }
  },
  
  // Auto-fill form from filter selection
  autoFillFromFilter: {
    filterField: 'selectedProductFilter',
    formField: 'ORIGIN_PRODUCT_ID'
  },
  
  // Display columns with foreign key lookups
  displayColumns: [
    {
      field: 'SERVICE_RESPONSE_ID',
      lookup: {
        table: 'SERVICE_RESPONSE',
        foreignKey: 'SERVICE_RESPONSE_ID',
        displayField: 'RESPONSE_PATH'
      }
    },
    {
      field: 'ORIGIN_PRODUCT_ID',
      lookup: {
        table: 'ORIGIN_PRODUCT',
        foreignKey: 'ORIGIN_PRODUCT_ID',
        displayField: 'PRODUCT_ID'
      }
    }
  ] as DisplayColumnConfig[],

  // Unified filter configuration
  filters: {
    table: [
      { type: 'custom', field: 'PSCU_CLIENT_ID', label: 'Filter by PSCU Client ID' },
      { type: 'product', field: 'ORIGIN_PRODUCT_ID', label: 'Filter by Product', required: true },
      { type: 'service', field: 'SERVICE_ID', label: 'Filter by Service', required: true }
    ] as FilterConfig[],
    modal: [
      { type: 'integrated', field: 'SERVICE_ID', label: 'Service', source: 'SERVICE' }
    ] as FilterConfig[],
    requiresService: true,
    requiresProduct: true,
    showEmptyUntilFiltered: true
  },

  // Fields configuration
  fields: [
    "SERVICE_RESPONSE_MAPPING_ID",
    "SERVICE_RESPONSE_ID",
    "ORIGIN_PRODUCT_ID",
    "OUTPUT_PATH"
  ],
  
  // Form fields configuration
  formFields: [
    {
      "name": "SERVICE_RESPONSE_ID",
      "type": "select",
      "required": true,
      "disabled": false,
      "options": []
    },
    {
      "name": "ORIGIN_PRODUCT_ID",
      "type": "select",
      "required": true,
      "disabled": false,
      "options": []
    },
    {
      "name": "OUTPUT_PATH",
      "type": "text",
      "required": true,
      "disabled": false
    }
  ]
}

export default config

